import React, { useState } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Title,
  Paper,
  Alert,
  Badge,
  FileInput,
  Table,
  ScrollArea,
  LoadingOverlay,
  Divider
} from '@mantine/core';
import {
  IconInfoCircle,
  IconDownload,
  IconFileImport,
  IconCheck,
  IconX,
  IconAlertTriangle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import * as XLSX from 'xlsx';

interface CourseInstance {
  instanceId: string;
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType: string;
  planningStart: string;
  planningEnd: string;
  trLCMSPlanningCoursesID: string;
}

/* eslint-disable */
interface ScheduleImportExportModalProps {
  opened: boolean;
  onClose: () => void;
  courseInstances: CourseInstance[];
  onImport: (instances: CourseInstance[]) => void;
  schedulePlanName: string;
  availableCourses: CourseInstance[];
}
/* eslint-enable */

interface ParsedScheduleRow {
  courseCode: string;
  courseName: string;
  startDate: string;
  endDate: string;
  learningHours: number;
  deliveryType: string;
  isValid: boolean;
  errors: string[];
  matchedCourse?: CourseInstance;
}

const ScheduleImportExportModal: React.FC<ScheduleImportExportModalProps> = ({
  opened,
  onClose,
  courseInstances,
  onImport,
  schedulePlanName,
  availableCourses
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importedSchedules, setImportedSchedules] = useState<ParsedScheduleRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<'main' | 'import'>('main');

  const handleExport = () => {
    try {
      const exportData = courseInstances.map((instance) => ({
        'Course Code': instance.courseCode,
        'Course Name': instance.courseName,
        'Start Date': instance.planningStart
          ? new Date(instance.planningStart).toLocaleDateString('en-US')
          : '',
        'End Date': instance.planningEnd
          ? new Date(instance.planningEnd).toLocaleDateString('en-US')
          : '',
        'Learning Hours': instance.learningHours,
        'Delivery Type': instance.deliveryType
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();

      // Set column widths
      const colWidths = [
        { wch: 15 }, // Course Code
        { wch: 40 }, // Course Name
        { wch: 15 }, // Start Date
        { wch: 15 }, // End Date
        { wch: 15 }, // Learning Hours
        { wch: 15 } // Delivery Type
      ];
      worksheet['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Course Schedule');

      const fileName = `${schedulePlanName.replace(/[^a-z0-9]/gi, '_')}_Schedule_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      notifications.show({
        title: 'Export Successful',
        message: `Schedule exported as "${fileName}"`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

      onClose();
    } catch {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export course schedule',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  };

  /* eslint-disable */
  const validateScheduleRow = (
    rowData: any,
    index: number
  ): { isValid: boolean; errors: string[]; matchedCourse?: CourseInstance } => {
    const errors: string[] = [];
    let matchedCourse: CourseInstance | undefined;

    if (!rowData['Course Code'] || rowData['Course Code'].trim() === '') {
      errors.push('Course Code is required');
    } else {
      // Check if course exists in available courses
      matchedCourse = availableCourses.find(
        (course) => course.courseCode.toLowerCase() === rowData['Course Code'].trim().toLowerCase()
      );

      if (!matchedCourse) {
        errors.push(`Course "${rowData['Course Code']}" is not available in this schedule plan`);
      }
    }

    if (!rowData['Start Date'] || rowData['Start Date'].trim() === '') {
      errors.push('Start Date is required');
    } else {
      try {
        const startDate = new Date(rowData['Start Date']);
        if (isNaN(startDate.getTime())) {
          errors.push('Invalid Start Date format');
        }
      } catch {
        errors.push('Invalid Start Date format');
      }
    }

    if (!rowData['End Date'] || rowData['End Date'].trim() === '') {
      errors.push('End Date is required');
    } else {
      try {
        const endDate = new Date(rowData['End Date']);
        if (isNaN(endDate.getTime())) {
          errors.push('Invalid End Date format');
        }
      } catch {
        errors.push('Invalid End Date format');
      }
    }

    // Date range validation
    if (rowData['Start Date'] && rowData['End Date']) {
      try {
        const startDate = new Date(rowData['Start Date']);
        const endDate = new Date(rowData['End Date']);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate >= endDate) {
          errors.push('End Date must be after Start Date');
        }
      } catch {
        // Already handled above
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      matchedCourse
    };
  };
  /* eslint-enable */

  const processFile = async (uploadedFile: File) => {
    if (!uploadedFile) return;

    setIsProcessing(true);
    try {
      const arrayBuffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Process and validate schedules
      const processedSchedules: ParsedScheduleRow[] = jsonData
        .filter((row: any) => row['Course Code'] && row['Course Code'].trim() !== '') // Filter empty rows
        .map((row: any, index: number) => {
          const validation = validateScheduleRow(row, index);

          return {
            courseCode: row['Course Code'] || '',
            courseName: row['Course Name'] || validation.matchedCourse?.courseName || '',
            startDate: row['Start Date'] ? new Date(row['Start Date']).toISOString() : '',
            endDate: row['End Date'] ? new Date(row['End Date']).toISOString() : '',
            learningHours:
              validation.matchedCourse?.learningHours || Number(row['Learning Hours']) || 0,
            deliveryType: validation.matchedCourse?.deliveryType || row['Delivery Type'] || '',
            isValid: validation.isValid,
            errors: validation.errors,
            matchedCourse: validation.matchedCourse
          };
        });

      setImportedSchedules(processedSchedules);

      const validCount = processedSchedules.filter((s) => s.isValid).length;
      const invalidCount = processedSchedules.length - validCount;

      if (invalidCount > 0) {
        notifications.show({
          title: 'Import Validation',
          message: `${validCount} valid schedules, ${invalidCount} with errors. Please review before importing.`,
          color: 'yellow',
          icon: <IconAlertTriangle />
        });
      } else {
        notifications.show({
          title: 'Import Success',
          message: `${validCount} schedules imported successfully`,
          color: 'green',
          icon: <IconCheck />
        });
      }
    } catch {
      notifications.show({
        title: 'Import Error',
        message: 'Failed to process the uploaded file. Please check the file format.',
        color: 'red',
        icon: <IconX />
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = (uploadedFile: File | null) => {
    setFile(uploadedFile);
    if (uploadedFile) {
      processFile(uploadedFile);
    } else {
      setImportedSchedules([]);
    }
  };

  const handleSubmitImport = () => {
    const validSchedules = importedSchedules.filter((s) => s.isValid);
    if (validSchedules.length === 0) {
      notifications.show({
        title: 'No Valid Schedules',
        message: 'Please ensure at least one schedule is valid before importing',
        color: 'red',
        icon: <IconX />
      });
      return;
    }

    // Convert to CourseInstance format
    const newInstances: CourseInstance[] = validSchedules.map((schedule, index) => ({
      instanceId: `imported-${Date.now()}-${index}`,
      msLCMSCourseID: schedule.matchedCourse?.msLCMSCourseID || `course-${schedule.courseCode}`,
      courseName: schedule.courseName,
      courseCode: schedule.courseCode,
      learningHours: schedule.learningHours,
      deliveryType: schedule.deliveryType,
      planningStart: schedule.startDate,
      planningEnd: schedule.endDate,
      trLCMSPlanningCoursesID: ''
    }));

    onImport(newInstances);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setImportedSchedules([]);
    setIsProcessing(false);
    setCurrentStep('main');
    onClose();
  };

  const validSchedulesCount = importedSchedules.filter((s) => s.isValid).length;
  const invalidSchedulesCount = importedSchedules.length - validSchedulesCount;

  const renderMainStep = () => (
    <Stack gap='lg'>
      <Alert icon={<IconInfoCircle size={16} />} title='Schedule Management' color='blue'>
        Manage course schedules for <b>{schedulePlanName}</b>. You can export current schedules or
        import new ones from Excel.
      </Alert>

      {/* Export Section */}
      <Paper p='lg' withBorder radius='lg'>
        <Stack gap='md'>
          <Group justify='space-between' align='center'>
            <div>
              <Text fw={600} size='lg' mb={4}>
                Export Current Schedule
              </Text>
              <Text size='sm' c='dimmed'>
                Download the current course schedule as an Excel file
              </Text>
            </div>
            <Badge variant='light' size='lg'>
              {courseInstances.length} courses
            </Badge>
          </Group>

          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
            variant='outline'
            color='#7B2183'
            size='md'
            fullWidth
          >
            Export to Excel
          </Button>
        </Stack>
      </Paper>

      <Divider label='OR' labelPosition='center' />

      {/* Import Section */}
      <Paper p='lg' withBorder radius='lg'>
        <Stack gap='md'>
          <div>
            <Text fw={600} size='lg' mb={4}>
              Import Schedule from Excel
            </Text>
            <Text size='sm' c='dimmed'>
              Upload an Excel file with course schedules. Only courses available in this plan can be
              scheduled.
            </Text>
          </div>

          <Button
            leftSection={<IconFileImport size={16} />}
            onClick={() => setCurrentStep('import')}
            color='#7B2183'
            size='md'
            fullWidth
          >
            Import from Excel
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );

  const renderImportStep = () => (
    <Stack gap='md'>
      <Group justify='space-between' align='center'>
        <Title order={4}>Import Course Schedule</Title>
        <Button
          variant='subtle'
          onClick={() => setCurrentStep('main')}
          leftSection={<IconX size={16} />}
        >
          Back
        </Button>
      </Group>

      <Alert icon={<IconInfoCircle size={16} />} title='Import Information' color='blue'>
        Upload an Excel file containing course schedules. The system will validate that all courses
        exist in the current plan.
        <br />
        <br />
        <Text size='sm'>
          Available courses in this plan: <b>{availableCourses.length}</b>
        </Text>
      </Alert>

      <Divider label='Upload Your File' labelPosition='center' />

      {/* File Upload Section */}
      <FileInput
        label='Upload Excel File'
        description='Upload an .xlsx or .xls file containing your course schedules'
        placeholder='Click to select file'
        accept='.xlsx,.xls'
        value={file}
        onChange={handleFileUpload}
        leftSection={<IconFileImport size={16} />}
      />

      {/* Preview and Validation Results */}
      {importedSchedules.length > 0 && (
        <>
          <Group justify='space-between' align='center'>
            <Text fw={600} size='lg'>
              Import Preview
            </Text>
            <Group gap='xs'>
              <Badge color='green' variant='light'>
                {validSchedulesCount} Valid
              </Badge>
              {invalidSchedulesCount > 0 && (
                <Badge color='red' variant='light'>
                  {invalidSchedulesCount} Invalid
                </Badge>
              )}
            </Group>
          </Group>

          <ScrollArea h={300}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '50px' }}>Status</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Course Name</Table.Th>
                  <Table.Th>Start Date</Table.Th>
                  <Table.Th>End Date</Table.Th>
                  <Table.Th>Errors</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {importedSchedules.map((schedule, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      {schedule.isValid ? (
                        <IconCheck size={16} color='green' />
                      ) : (
                        <IconX size={16} color='red' />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm' fw={500}>
                        {schedule.courseCode}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm' lineClamp={1}>
                        {schedule.courseName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>
                        {schedule.startDate
                          ? new Date(schedule.startDate).toLocaleDateString()
                          : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size='sm'>
                        {schedule.endDate ? new Date(schedule.endDate).toLocaleDateString() : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {schedule.errors.length > 0 && (
                        <Text size='xs' c='red'>
                          {schedule.errors.join(', ')}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          {invalidSchedulesCount > 0 && (
            <Alert icon={<IconAlertTriangle size={16} />} title='Validation Errors' color='yellow'>
              {invalidSchedulesCount} schedule(s) have validation errors. Only valid schedules will
              be imported. Common issues: course not available in plan, invalid date format, or
              missing required fields.
            </Alert>
          )}

          <Group justify='flex-end' mt='md'>
            <Button variant='outline' color='#7B2183' onClick={() => setCurrentStep('main')}>
              Cancel
            </Button>
            <Button
              color='#7B2183'
              onClick={handleSubmitImport}
              disabled={validSchedulesCount === 0}
              leftSection={<IconFileImport size={16} />}
            >
              Import {validSchedulesCount} Schedule{validSchedulesCount !== 1 ? 's' : ''}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton={false}
      size='xl'
      centered
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <div style={{ position: 'relative' }}>
        <LoadingOverlay visible={isProcessing} overlayProps={{ radius: 'sm', blur: 2 }} />

        <Stack gap='md' p={24}>
          <Group justify='space-between' align='center'>
            <Title order={3}>
              {currentStep === 'main' ? 'Schedule Management' : 'Import Schedule'}
            </Title>
            <Button variant='subtle' onClick={handleClose}>
              <IconX size={16} />
            </Button>
          </Group>

          {currentStep === 'main' ? renderMainStep() : renderImportStep()}
        </Stack>
      </div>
    </Modal>
  );
};

export default ScheduleImportExportModal;
